import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import { format } from 'date-fns';
import DatePickerButton from './DatePickerButton';
import { X, Plane, Building, Search, Users, ExternalLink, FileText, Luggage } from 'lucide-react';

// City Search with Google Places API
const CitySearch = ({ value, onChange, placeholder }) => {
  const [inputValue, setInputValue] = useState(value || '');

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue);
  };

  const initializeGooglePlaces = (input) => {
    if (window.google && window.google.maps && window.google.maps.places && !input.autocompleteInitialized) {
      const autocomplete = new window.google.maps.places.Autocomplete(input, {
        types: ['(cities)'],
        fields: ['place_id', 'name', 'formatted_address', 'geometry', 'address_components']
      });
      
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place && place.name) {
          setInputValue(place.name);
          onChange(place.name);
        }
      });
      
      input.autocompleteInitialized = true;
    }
  };

  return (
    <input
      type="text"
      value={inputValue}
      onChange={handleInputChange}
      onFocus={(e) => initializeGooglePlaces(e.target)}
      className="w-full border border-ui-control rounded-md px-3 py-2 text-sm"
      placeholder={placeholder || "Search cities..."}
      autoComplete="off"
    />
  );
};
const AirportSearch = ({ value, onChange, placeholder }) => {
  const [searchTerm, setSearchTerm] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isSelectingFromDropdown, setIsSelectingFromDropdown] = useState(false);

  const searchAirports = async (query) => {
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const response = await axios.get(`${apiBase}/search/airports?keyword=${encodeURIComponent(query)}`, { headers });
      
      const airports = Array.isArray(response.data) ? response.data : [];
      setSuggestions(airports);
    } catch (error) {
      console.error('Error searching airports:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    setSearchTerm(inputValue);
    setShowSuggestions(true);
    setIsSelectingFromDropdown(false);
    
    onChange(inputValue);
    
    clearTimeout(window.airportSearchTimeout);
    window.airportSearchTimeout = setTimeout(() => {
      searchAirports(inputValue);
    }, 300);
  };

  const handleSelectAirport = (airport) => {
    const airportCode = airport.iata_code || airport.icao_code;
    const airportDisplay = `${airportCode} - ${airport.name}`;
    
    setIsSelectingFromDropdown(true);
    setSearchTerm(airportDisplay);
    setShowSuggestions(false);
    setSuggestions([]);
    
    onChange(airportCode);
  };

  const handleBlur = () => {
    if (!isSelectingFromDropdown) {
      onChange(searchTerm);
    }
    setTimeout(() => {
      setIsSelectingFromDropdown(false);
      setShowSuggestions(false);
    }, 200);
  };

  return (
    <div className="relative">
      <input
        type="text"
        value={searchTerm}
        onChange={handleInputChange}
        onBlur={handleBlur}
        onFocus={() => setShowSuggestions(true)}
        className="w-full border border-ui-control rounded-md px-3 py-2 text-sm"
        placeholder={placeholder || "Search airports..."}
        autoComplete="off"
      />
      
      {showSuggestions && (suggestions.length > 0 || loading) && (
        <div className="absolute z-50 mt-1 w-full bg-ui-surface border border-ui-control rounded-md shadow-lg max-h-60 overflow-auto">
          {loading && (
            <div className="px-3 py-2 text-sm text-ui-muted">
              Searching airports...
            </div>
          )}
          
          {!loading && suggestions.map((airport) => (
            <button
              key={airport.iata_code || airport.icao_code}
              onMouseDown={(e) => {
                e.preventDefault();
              }}
              onClick={() => handleSelectAirport(airport)}
              className="w-full text-left px-3 py-2 hover:bg-ui-hover focus:bg-ui-hover focus:outline-none"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-sm">
                    {airport.iata_code || airport.icao_code} - {airport.name}
                  </div>
                  <div className="text-xs text-ui-muted">
                    {airport.municipality}, {airport.iso_country}
                  </div>
                </div>
                <div className="text-xs text-ui-muted">
                  {airport.icao_code}
                </div>
              </div>
            </button>
          ))}
          
          {!loading && suggestions.length === 0 && searchTerm.length >= 2 && (
            <div className="px-3 py-2 text-sm text-ui-muted">
              No airports found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const TripBookingModal = ({ tripId, trip, onClose, onSuccess }) => {
  const [activeTab, setActiveTab] = useState('flights');
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [selectedFlight, setSelectedFlight] = useState(null);
  const [selectedHotel, setSelectedHotel] = useState(null);   // Add hotel selection state
  const [bookingStep, setBookingStep] = useState('search');
  const [error, setError] = useState('');
  const [selectedPassengers, setSelectedPassengers] = useState([]);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsType, setTermsType] = useState('');
  const [airlineCode, setAirlineCode] = useState('');
  
  const [flightSearch, setFlightSearch] = useState({
    origin: '',
    destination: '',
    departure_date: '',
    return_date: '',
    adults: 1,
    travel_class: 'ECONOMY'
  });

  const [hotelSearch, setHotelSearch] = useState({
    city: '',
    check_in_date: '',
    check_out_date: '',
    adults: 1,
    room_quantity: 1
  });

  // Load trip executives for passenger selection
  const [tripExecutives, setTripExecutives] = useState([]);
  
  useEffect(() => {
    const loadTripExecutives = async () => {
      try {
        const apiBase = getApiBase();
        const headers = getAuthHeaders();
        
        const response = await axios.get(`${apiBase}/executives`, { headers });
        const allExecutives = response.data || [];
        
        const assignedExecutives = trip?.executive_ids ? 
          allExecutives.filter(exec => trip.executive_ids.includes(exec.id)) :
          allExecutives.filter(exec => {
            const fullName = exec.full_name || `${exec.first_name} ${exec.last_name}`;
            return trip?.executive_names?.includes(fullName);
          });
        
        setTripExecutives(assignedExecutives);
        
        if (assignedExecutives.length === 1) {
          setSelectedPassengers([assignedExecutives[0].id]);
        }
      } catch (error) {
        console.error('Error loading trip executives:', error);
        setTripExecutives([]);
      }
    };

    if (trip) {
      loadTripExecutives();
    }
  }, [trip]);

  const handleSelectFlight = (flight) => {
    setSelectedFlight(flight);
    setBookingStep('details');
  };

  const handleSelectHotel = (hotel) => {
    setSelectedHotel(hotel);
    setBookingStep('details');
  };

  const handleBackToSearch = () => {
    setSelectedFlight(null);
    setSelectedHotel(null);
    setBookingStep('search');
  };

  const searchFlights = async () => {
    setLoading(true);
    setError('');
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      
      const response = await axios.get(`${apiBase}/search/flights`, {
        params: flightSearch,
        headers: headers
      });
      
      if (response.data.success) {
        const duffelResponse = response.data.data;
        if (duffelResponse.success && duffelResponse.data) {
          setSearchResults(duffelResponse.data);
        } else {
          setError(duffelResponse.error || 'No flights found');
        }
      } else {
        setError(response.data.error || 'Search failed');
      }
    } catch (err) {
      setError('Error searching flights: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const bookFlightAsComponent = async (flight) => {
    setLoading(true);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      
      if (selectedPassengers.length === 0) {
        setError('Please select at least one passenger for the flight');
        setLoading(false);
        return;
      }
      
      const segments = flight.itineraries[0].segments;
      
      const passengers = selectedPassengers.map(passengerId => {
        const executive = tripExecutives.find(exec => exec.id === passengerId);
        return {
          name: `${executive.first_name} ${executive.last_name}`.toUpperCase(),
          executive_id: executive.id,
          confirmation_number: `EA${Date.now().toString().slice(-6)}`,
          frequent_flyer_number: '',
          ticket_number: ''
        };
      });
      
      const componentData = {
        component_type: 'commercial_flight',
        title: `${segments[0].departure.iataCode} → ${segments[segments.length - 1].arrival.iataCode}`,
        start_datetime: segments[0].departure.at,
        end_datetime: segments[segments.length - 1].arrival.at,
        location: `${segments[0].departure.iataCode} to ${segments[segments.length - 1].arrival.iataCode}`,
        status: 'confirmed',
        confirmation_number: `EA${Date.now().toString().slice(-6)}`,
        component_data: {
          assigned_executives: selectedPassengers,
          passengers: passengers,
          flight_legs: segments.map((segment, index) => ({
            airline_name: segment.carrierCode,
            flight_number: segment.number,
            departure_airport: segment.departure.iataCode,
            departure_time: segment.departure.at,
            arrival_airport: segment.arrival.iataCode,
            arrival_time: segment.arrival.at,
            aircraft_type: segment.aircraft?.code || '',
            class_type: flight.travelerPricings[0]?.fareDetailsBySegment[0]?.cabin || 'ECONOMY'
          })),
          total_price: flight.price.total,
          currency: flight.price.currency,
          duffel_offer_id: flight.duffel_offer_id || flight.id
        }
      };

      await axios.post(`${apiBase}/trips/${tripId}/components`, componentData, { headers });
      
      onSuccess();
      onClose();
    } catch (error) {
      setError('Failed to add flight to trip: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const searchHotels = async () => {
    setLoading(true);
    setError('');
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      
      const response = await axios.get(`${apiBase}/search/hotels`, {
        params: hotelSearch,
        headers: headers
      });
      
      if (response.data.success) {
        const duffelResponse = response.data.data;
        if (duffelResponse.success && duffelResponse.data) {
          setSearchResults(duffelResponse.data);
        } else {
          setError(duffelResponse.error || 'No hotels found');
        }
      } else {
        setError(response.data.error || 'Hotel search failed');
      }
    } catch (err) {
      setError('Error searching hotels: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const bookHotelAsComponent = async (hotel) => {
    setLoading(true);
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      
      if (selectedPassengers.length === 0) {
        setError('Please select at least one guest for the hotel');
        setLoading(false);
        return;
      }
      
      const guests = selectedPassengers.map(passengerId => {
        const executive = tripExecutives.find(exec => exec.id === passengerId);
        return {
          name: `${executive.first_name} ${executive.last_name}`.toUpperCase(),
          executive_id: executive.id,
          confirmation_number: `EH${Date.now().toString().slice(-6)}`
        };
      });
      
      const componentData = {
        component_type: 'hotel',
        title: hotel.hotel.name,
        start_datetime: hotelSearch.check_in_date + 'T15:00:00',
        end_datetime: hotelSearch.check_out_date + 'T11:00:00',
        location: `${hotel.hotel.address?.cityName || hotelSearch.city}`,
        status: 'confirmed',
        confirmation_number: `EH${Date.now().toString().slice(-6)}`,
        component_data: {
          assigned_executives: selectedPassengers,
          guests: guests,
          hotel_name: hotel.hotel.name,
          hotel_address: hotel.hotel.address?.lines?.join(', ') || '',
          city: hotel.hotel.address?.cityName || hotelSearch.city,
          country: hotel.hotel.address?.countryCode || '',
          check_in: hotelSearch.check_in_date + 'T15:00:00',  // Use check_in (not check_in_date)
          check_out: hotelSearch.check_out_date + 'T11:00:00', // Use check_out (not check_out_date)
          room_type: hotel.offers[0]?.room?.type?.description || 'Standard Room',
          total_price: hotel.offers[0]?.price?.total || '0',
          currency: hotel.offers[0]?.price?.currency || 'USD',
          duffel_offer_id: hotel.duffel_stay_id || hotel.offers[0]?.id
        }
      };

      await axios.post(`${apiBase}/trips/${tripId}/components`, componentData, { headers });
      
      onSuccess();
      onClose();
    } catch (error) {
      setError('Failed to add hotel to trip: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-ui-ink bg-opacity-50 h-full w-full z-[70] flex items-center justify-center">
      <div className="bg-ui-surface rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-ui-green-bg rounded-full flex items-center justify-center mr-3">
                <Search className="w-5 h-5 text-ui-green-fg" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-ui-ink">Book Travel for Trip</h3>
                <p className="text-sm text-ui-muted">Search and book flights or hotels for this trip</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-ui-muted hover:text-ui-ink-3 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {error && (
            <div className="bg-ui-red-bg border border-ui-red-line rounded-md p-3 mb-4">
              <div className="text-ui-red-fg text-sm">{error}</div>
            </div>
          )}

          {/* Tabs */}
          <div className="mb-6">
            <nav className="flex space-x-8">
              <button
                onClick={() => setActiveTab('flights')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'flights'
                    ? 'border-ui-primary text-ui-link'
                    : 'border-transparent text-ui-muted hover:text-ui-ink-2'
                }`}
              >
                <Plane className="w-4 h-4 inline mr-2" />
                Flights
              </button>
              <button
                onClick={() => setActiveTab('hotels')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'hotels'
                    ? 'border-ui-primary text-ui-link'
                    : 'border-transparent text-ui-muted hover:text-ui-ink-2'
                }`}
              >
                <Building className="w-4 h-4 inline mr-2" />
                Hotels
              </button>
            </nav>
          </div>

          {/* Content */}
          <div>
            {/* Flight Search */}
            {activeTab === 'flights' && bookingStep === 'search' && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-ui-ink-2 mb-1">Origin Airport</label>
                    <AirportSearch
                      value={flightSearch.origin}
                      onChange={(value) => setFlightSearch(prev => ({ ...prev, origin: value }))}
                      placeholder="e.g., JFK, New York"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ui-ink-2 mb-1">Destination Airport</label>
                    <AirportSearch
                      value={flightSearch.destination}
                      onChange={(value) => setFlightSearch(prev => ({ ...prev, destination: value }))}
                      placeholder="e.g., LAX, Los Angeles"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-ui-ink-2 mb-1">Departure Date</label>
                    <DatePickerButton
                      label={flightSearch.departure_date ? 
                        format(new Date(flightSearch.departure_date), 'MMM dd, yyyy') : 
                        'Select departure date'
                      }
                      selectedDate={flightSearch.departure_date ? new Date(flightSearch.departure_date) : null}
                      onPick={(date) => {
                        const isoDate = date ? format(date, 'yyyy-MM-dd') : '';
                        setFlightSearch(prev => ({ ...prev, departure_date: isoDate }));
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ui-ink-2 mb-1">Return Date (Optional)</label>
                    <DatePickerButton
                      label={flightSearch.return_date ? 
                        format(new Date(flightSearch.return_date), 'MMM dd, yyyy') : 
                        'Select return date'
                      }
                      selectedDate={flightSearch.return_date ? new Date(flightSearch.return_date) : null}
                      onPick={(date) => {
                        const isoDate = date ? format(date, 'yyyy-MM-dd') : '';
                        setFlightSearch(prev => ({ ...prev, return_date: isoDate }));
                      }}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-ui-ink-2 mb-1">Adults</label>
                    <select
                      value={flightSearch.adults}
                      onChange={(e) => setFlightSearch(prev => ({ ...prev, adults: parseInt(e.target.value) }))}
                      className="w-full border border-ui-control rounded-md px-3 py-2 text-sm"
                    >
                      {[1,2,3,4,5,6,7,8,9].map(n => (
                        <option key={n} value={n}>{n} Adult{n > 1 ? 's' : ''}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ui-ink-2 mb-1">Class</label>
                    <select
                      value={flightSearch.travel_class}
                      onChange={(e) => setFlightSearch(prev => ({ ...prev, travel_class: e.target.value }))}
                      className="w-full border border-ui-control rounded-md px-3 py-2 text-sm"
                    >
                      <option value="ECONOMY">Economy</option>
                      <option value="PREMIUM_ECONOMY">Premium Economy</option>
                      <option value="BUSINESS">Business</option>
                      <option value="FIRST">First</option>
                    </select>
                  </div>
                </div>

                <button
                  onClick={searchFlights}
                  disabled={loading || !flightSearch.origin || !flightSearch.destination || !flightSearch.departure_date}
                  className="w-full bg-ui-primary text-ui-on-primary py-3 px-4 rounded-md hover:bg-ui-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Searching...' : 'Search Flights'}
                </button>

                {/* Flight Search Results */}
                {searchResults && searchResults.data && (
                  <div className="mt-6 space-y-4">
                    <h4 className="text-lg font-semibold">Available Flights</h4>
                    {searchResults.data.map((flight, index) => {
                      const segments = flight.itineraries[0].segments;
                      const firstSegment = segments[0];
                      const lastSegment = segments[segments.length - 1];
                      const travelerPricing = flight.travelerPricings[0];
                      
                      return (
                        <div key={flight.id} className="border border-ui-line rounded-lg p-4">
                          <div className="flex justify-between items-start mb-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-4 mb-2">
                                <div className="text-sm font-medium">
                                  {firstSegment.departure.iataCode} → {lastSegment.arrival.iataCode}
                                </div>
                                <div className="text-sm text-ui-muted">
                                  {flight.itineraries[0].duration.replace('PT', '').replace('H', 'h ').replace('M', 'm')}
                                </div>
                                <div className="text-sm text-ui-muted">
                                  {segments.length > 1 ? `${segments.length - 1} stop${segments.length > 2 ? 's' : ''}` : 'Nonstop'}
                                </div>
                              </div>
                              <div className="text-sm text-ui-ink-3 mb-2">
                                {format(new Date(firstSegment.departure.at), 'h:mm a')} - {format(new Date(lastSegment.arrival.at), 'h:mm a')}
                              </div>
                              <div className="text-xs text-ui-muted mb-3">
                                {firstSegment.carrierCode} {firstSegment.number}
                                {segments.length > 1 && ` • ${segments.length - 1} stop${segments.length > 2 ? 's' : ''}`}
                              </div>

                              {/* Baggage Information */}
                              <div className="space-y-2 text-sm text-ui-ink-3">
                                <div className="flex items-center">
                                  <span className="text-ui-green-fg mr-2">✓</span>
                                  Personal item included
                                </div>
                                <div className="flex items-center">
                                  <span className="text-ui-green-fg mr-2">✓</span>
                                  {travelerPricing.fareDetailsBySegment[0]?.includedCabinBags?.quantity || 1} carry-on
                                </div>
                                <div className="flex items-center">
                                  <span className="text-ui-orange-fg mr-2">+</span>
                                  Checked bag from $25
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-lg font-semibold text-ui-green-fg">
                                ${flight.price.total}
                              </div>
                              <div className="text-sm text-ui-muted mb-2">per person</div>
                              <button
                                onClick={() => handleSelectFlight(flight)}
                                disabled={loading}
                                className="bg-ui-primary text-ui-on-primary px-4 py-2 rounded text-sm hover:bg-ui-primary-hover disabled:opacity-50"
                              >
                                Select Flight
                              </button>
                            </div>
                          </div>

                          {/* Terms and Conditions Links */}
                          <div className="border-t pt-3 mt-3">
                            <div className="flex flex-wrap gap-3 text-xs">
                              <button
                                onClick={() => {
                                  setTermsType('terms');
                                  setAirlineCode(firstSegment.carrierCode);
                                  setShowTermsModal(true);
                                }}
                                className="text-ui-link hover:text-ui-link underline"
                              >
                                Contract of Carriage
                              </button>
                              <button
                                onClick={() => {
                                  setTermsType('baggage');
                                  setAirlineCode(firstSegment.carrierCode);
                                  setShowTermsModal(true);
                                }}
                                className="text-ui-link hover:text-ui-link underline"
                              >
                                Baggage Policy
                              </button>
                              <button
                                onClick={() => {
                                  setTermsType('cancellation');
                                  setAirlineCode(firstSegment.carrierCode);
                                  setShowTermsModal(true);
                                }}
                                className="text-ui-link hover:text-ui-link underline"
                              >
                                Cancellation Policy
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Flight Details View */}
            {bookingStep === 'details' && selectedFlight && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h4 className="text-lg font-semibold">Flight Details</h4>
                  <button
                    onClick={handleBackToSearch}
                    className="text-ui-link hover:text-ui-link text-sm"
                  >
                    ← Back to Search
                  </button>
                </div>
                
                {/* Detailed Flight Information */}
                <div className="bg-ui-sunk rounded-lg p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Flight Overview */}
                    <div>
                      <h5 className="font-semibold text-ui-ink mb-3">Flight Overview</h5>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-ui-ink-3">Route:</span>
                          <span className="font-medium">
                            {selectedFlight.itineraries[0].segments[0].departure.iataCode} → {selectedFlight.itineraries[0].segments[selectedFlight.itineraries[0].segments.length - 1].arrival.iataCode}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-ui-ink-3">Duration:</span>
                          <span className="font-medium">
                            {selectedFlight.itineraries[0].duration.replace('PT', '').replace('H', 'h ').replace('M', 'm')}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-ui-ink-3">Stops:</span>
                          <span className="font-medium">
                            {selectedFlight.itineraries[0].segments.length > 1 ? `${selectedFlight.itineraries[0].segments.length - 1} stop${selectedFlight.itineraries[0].segments.length > 2 ? 's' : ''}` : 'Nonstop'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Pricing Information */}
                    <div>
                      <h5 className="font-semibold text-ui-ink mb-3">Pricing</h5>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-ui-ink-3">Base Price:</span>
                          <span className="font-medium">${selectedFlight.price.base}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-ui-ink-3">Total:</span>
                          <span className="text-ui-green-fg font-bold text-lg">${selectedFlight.price.total}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Flight Segments */}
                  <div className="mt-6">
                    <h5 className="font-semibold text-ui-ink mb-3">Flight Segments</h5>
                    <div className="space-y-3">
                      {selectedFlight.itineraries[0].segments.map((segment, index) => (
                        <div key={index} className="bg-ui-surface rounded-lg p-4 border border-ui-line">
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="font-medium text-sm text-ui-ink">
                                {segment.carrierCode} {segment.number}
                              </div>
                              <div className="text-xs text-ui-muted mt-1">
                                {segment.departure.iataCode} → {segment.arrival.iataCode}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-medium">
                                {format(new Date(segment.departure.at), 'h:mm a')} - {format(new Date(segment.arrival.at), 'h:mm a')}
                              </div>
                              <div className="text-xs text-ui-muted">
                                {format(new Date(segment.departure.at), 'MMM dd, yyyy')}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Passenger Selection for Booking */}
                <div className="bg-ui-blue-bg rounded-lg p-4 border border-ui-blue-line">
                  <h4 className="text-sm font-semibold text-ui-ink mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4 text-ui-link" />
                    Select Passengers for This Flight
                  </h4>
                  <div className="space-y-2">
                    {tripExecutives.length === 0 ? (
                      <div className="text-sm text-ui-muted">
                        No executives assigned to this trip. Please assign executives to the trip first.
                      </div>
                    ) : (
                      tripExecutives.map(executive => (
                        <label key={executive.id} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={selectedPassengers.includes(executive.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedPassengers(prev => [...prev, executive.id]);
                              } else {
                                setSelectedPassengers(prev => prev.filter(id => id !== executive.id));
                              }
                            }}
                            className="h-4 w-4 text-ui-link border-ui-control rounded focus:ring-ui-primary"
                          />
                          <span className="ml-2 text-sm text-ui-ink">
                            {executive.first_name} {executive.last_name}
                            {executive.title && ` - ${executive.title}`}
                          </span>
                        </label>
                      ))
                    )}
                  </div>
                  <p className="text-xs text-ui-muted mt-2">
                    Select which executives will be passengers on this flight
                  </p>
                </div>

                {/* Terms and Important Information */}
                <div className="bg-ui-amber-bg rounded-lg p-4 border border-ui-amber-line">
                  <h4 className="text-sm font-semibold text-ui-ink mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-ui-amber-fg" />
                    Important Information
                  </h4>
                  <div className="space-y-2 text-xs text-ui-ink-2">
                    <p>• Please arrive at the airport according to airline recommendations.</p>
                    <p>• Ticket is valid only for the named passenger and cannot be transferred.</p>
                    <p>• Baggage allowances and restrictions apply. Check baggage policy for details.</p>
                    <p>• Flight schedules are subject to change. Please confirm before travel.</p>
                  </div>
                  
                  <div className="flex gap-3 mt-3 text-xs">
                    <button
                      onClick={() => {
                        setTermsType('terms');
                        setAirlineCode(selectedFlight.itineraries[0].segments[0].carrierCode);
                        setShowTermsModal(true);
                      }}
                      className="text-ui-link hover:text-ui-link underline flex items-center gap-1"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Full Terms
                    </button>
                    <button
                      onClick={() => {
                        setTermsType('baggage');
                        setAirlineCode(selectedFlight.itineraries[0].segments[0].carrierCode);
                        setShowTermsModal(true);
                      }}
                      className="text-ui-link hover:text-ui-link underline flex items-center gap-1"
                    >
                      <Luggage className="w-3 h-3" />
                      Baggage Policy
                    </button>
                  </div>
                </div>

                {/* Final Booking Button */}
                <div className="flex justify-between items-center pt-4 border-t">
                  <div className="text-sm text-ui-ink-3">
                    Total: <span className="text-lg font-bold text-ui-green-fg">${selectedFlight.price.total}</span>
                    {selectedPassengers.length > 0 && (
                      <span className="ml-2">for {selectedPassengers.length} passenger{selectedPassengers.length > 1 ? 's' : ''}</span>
                    )}
                  </div>
                  <button
                    onClick={() => bookFlightAsComponent(selectedFlight)}
                    disabled={loading || selectedPassengers.length === 0}
                    className="bg-ui-green-strong text-ui-on-primary px-6 py-3 rounded-md hover:bg-ui-green-strong/90 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
                  >
                    {loading ? 'Booking...' : 'Book Flight & Add to Trip'}
                  </button>
                </div>
              </div>
            )}

            {/* Hotel Search */}
            {activeTab === 'hotels' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-ui-ink-2 mb-1">City</label>
                  <CitySearch
                    value={hotelSearch.city}
                    onChange={(value) => setHotelSearch(prev => ({ ...prev, city: value }))}
                    placeholder="e.g., New York, Paris, London"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-ui-ink-2 mb-1">Check-in Date</label>
                    <DatePickerButton
                      label={hotelSearch.check_in_date ? 
                        format(new Date(hotelSearch.check_in_date), 'MMM dd, yyyy') : 
                        'Select check-in date'
                      }
                      selectedDate={hotelSearch.check_in_date ? new Date(hotelSearch.check_in_date) : null}
                      onPick={(date) => {
                        const isoDate = date ? format(date, 'yyyy-MM-dd') : '';
                        setHotelSearch(prev => ({ ...prev, check_in_date: isoDate }));
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ui-ink-2 mb-1">Check-out Date</label>
                    <DatePickerButton
                      label={hotelSearch.check_out_date ? 
                        format(new Date(hotelSearch.check_out_date), 'MMM dd, yyyy') : 
                        'Select check-out date'
                      }
                      selectedDate={hotelSearch.check_out_date ? new Date(hotelSearch.check_out_date) : null}
                      onPick={(date) => {
                        const isoDate = date ? format(date, 'yyyy-MM-dd') : '';
                        setHotelSearch(prev => ({ ...prev, check_out_date: isoDate }));
                      }}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-ui-ink-2 mb-1">Adults</label>
                    <select
                      value={hotelSearch.adults}
                      onChange={(e) => setHotelSearch(prev => ({ ...prev, adults: parseInt(e.target.value) }))}
                      className="w-full border border-ui-control rounded-md px-3 py-2 text-sm"
                    >
                      {[1,2,3,4,5,6].map(n => (
                        <option key={n} value={n}>{n} Adult{n > 1 ? 's' : ''}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ui-ink-2 mb-1">Rooms</label>
                    <select
                      value={hotelSearch.room_quantity}
                      onChange={(e) => setHotelSearch(prev => ({ ...prev, room_quantity: parseInt(e.target.value) }))}
                      className="w-full border border-ui-control rounded-md px-3 py-2 text-sm"
                    >
                      {[1,2,3,4,5].map(n => (
                        <option key={n} value={n}>{n} Room{n > 1 ? 's' : ''}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <button
                  onClick={searchHotels}
                  disabled={loading || !hotelSearch.city || !hotelSearch.check_in_date || !hotelSearch.check_out_date}
                  className="w-full bg-ui-primary text-ui-on-primary py-3 px-4 rounded-md hover:bg-ui-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Searching...' : 'Search Hotels'}
                </button>

                {/* Hotel Search Results - Only show during search step */}
                {searchResults && searchResults.data && bookingStep === 'search' && (
                  <div className="mt-6 space-y-4">
                    <h4 className="text-lg font-semibold">Available Hotels</h4>
                    {searchResults.data.map((hotel, index) => {
                      const offer = hotel.offers[0];
                      const address = hotel.hotel.address;
                      
                      return (
                        <div key={hotel.hotel.hotelId} className="border border-ui-line rounded-lg p-4">
                          <div className="flex justify-between items-start mb-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-4 mb-2">
                                <div className="text-lg font-semibold text-ui-ink">
                                  {hotel.hotel.name}
                                </div>
                                {hotel.hotel.rating && (
                                  <div className="flex items-center">
                                    {[...Array(parseInt(hotel.hotel.rating))].map((_, i) => (
                                      <span key={i} className="text-ui-amber-fg">★</span>
                                    ))}
                                    <span className="ml-1 text-sm text-ui-muted">
                                      ({hotel.hotel.rating} stars)
                                    </span>
                                  </div>
                                )}
                              </div>
                              
                              {address && (
                                <div className="text-sm text-ui-ink-3 mb-2">
                                  {address.lines?.join(', ')}, {address.cityName}
                                  {address.countryCode && `, ${address.countryCode}`}
                                </div>
                              )}
                              
                              {offer && (
                                <div className="space-y-2 text-sm text-ui-ink-3">
                                  <div className="flex items-center">
                                    <span className="text-ui-green-fg mr-2">✓</span>
                                    {offer.room?.type?.description || 'Standard Room'}
                                  </div>
                                  <div className="flex items-center">
                                    <span className="text-ui-green-fg mr-2">✓</span>
                                    {offer.policies?.cancellation?.type === 'FREE_CANCELLATION' ? 
                                      'Free cancellation' : 
                                      'Cancellation policies apply'
                                    }
                                  </div>
                                  {offer.policies?.paymentType && (
                                    <div className="flex items-center">
                                      <span className="text-ui-link mr-2">ℹ</span>
                                      Payment: {offer.policies.paymentType.replace('_', ' ').toLowerCase()}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            
                            <div className="text-right">
                              {offer && (
                                <>
                                  <div className="text-lg font-semibold text-ui-green-fg">
                                    ${offer.price.total}
                                  </div>
                                  <div className="text-sm text-ui-muted mb-2">
                                    {offer.price.currency} total
                                  </div>
                                  <button
                                    onClick={() => handleSelectHotel(hotel)}
                                    disabled={loading}
                                    className="bg-ui-primary text-ui-on-primary px-4 py-2 rounded text-sm hover:bg-ui-primary-hover disabled:opacity-50"
                                  >
                                    Select Hotel
                                  </button>
                                </>
                              )}
                            </div>
                          </div>

                          {/* Hotel Details */}
                          {offer && (
                            <div className="border-t pt-3 mt-3">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs text-ui-ink-3">
                                <div>
                                  <span className="font-medium">Check-in:</span> {hotelSearch.check_in_date}
                                </div>
                                <div>
                                  <span className="font-medium">Check-out:</span> {hotelSearch.check_out_date}
                                </div>
                                <div>
                                  <span className="font-medium">Guests:</span> {hotelSearch.adults} adult{hotelSearch.adults > 1 ? 's' : ''}
                                </div>
                              </div>
                              
                              {offer.policies?.cancellation && (
                                <div className="mt-2 text-xs text-ui-muted">
                                  <span className="font-medium">Cancellation:</span> {
                                    offer.policies.cancellation.type === 'FREE_CANCELLATION' ?
                                      `Free until ${offer.policies.cancellation.deadline || 'check-in'}` :
                                      'Non-refundable or fees apply'
                                  }
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

          {/* Hotel Details View */}
          {bookingStep === 'details' && selectedHotel && activeTab === 'hotels' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-semibold">Hotel Details</h4>
                <button
                  onClick={handleBackToSearch}
                  className="text-ui-link hover:text-ui-link text-sm"
                >
                  ← Back to Search
                </button>
              </div>
              
              {/* Show ONLY the selected hotel */}
              <div className="bg-ui-sunk rounded-lg p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-ui-ink mb-2">{selectedHotel.hotel.name}</h3>
                    <div className="text-sm text-ui-ink-3 mb-4">
                      {selectedHotel.hotel.cityCode}
                    </div>
                    
                    {/* Hotel amenities and details */}
                    <div className="space-y-2 text-sm text-ui-ink-3">
                      <div className="flex items-center">
                        <span className="text-ui-green-fg mr-2">✓</span>
                        {selectedHotel.offers[0]?.room?.typeEstimated?.category?.replace(/_/g, ' ')?.toLowerCase()?.replace(/\b\w/g, l => l.toUpperCase()) || 'Standard Room'}
                      </div>
                      <div className="flex items-center">
                        <span className="text-ui-green-fg mr-2">✓</span>
                        {selectedHotel.offers[0]?.policies?.cancellation?.type === 'FREE_CANCELLATION' ? 
                          'Free cancellation' : 
                          'Cancellation policies apply'
                        }
                      </div>
                      <div className="flex items-center">
                        <span className="text-ui-link mr-2">ℹ</span>
                        Payment: {selectedHotel.offers[0]?.policies?.paymentType?.replace('_', ' ')?.toLowerCase() || 'guarantee'}
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className="text-2xl font-bold text-ui-green-fg">
                      ${selectedHotel.offers[0]?.price?.total}
                    </div>
                    <div className="text-sm text-ui-muted">USD total</div>
                  </div>
                </div>

                {/* Stay Details */}
                <div className="border-t pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-ui-ink-3">
                    <div>
                      <span className="font-medium">Check-in:</span> {format(new Date(hotelSearch.check_in_date), 'MMM dd, yyyy')}
                      <div className="text-xs text-ui-muted">3:00 PM</div>
                    </div>
                    <div>
                      <span className="font-medium">Check-out:</span> {format(new Date(hotelSearch.check_out_date), 'MMM dd, yyyy')}
                      <div className="text-xs text-ui-muted">11:00 AM</div>
                    </div>
                    <div>
                      <span className="font-medium">Guests:</span> {hotelSearch.adults} adult{hotelSearch.adults > 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
              </div>

              {/* Guest Selection for Hotel */}
              <div className="bg-ui-blue-bg rounded-lg p-4 border border-ui-blue-line">
                <h4 className="text-sm font-semibold text-ui-ink mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4 text-ui-link" />
                  Select Guests for This Hotel
                </h4>
                <div className="space-y-2">
                  {tripExecutives.length === 0 ? (
                    <div className="text-sm text-ui-muted">
                      No executives assigned to this trip. Please assign executives to the trip first.
                    </div>
                  ) : (
                    tripExecutives.map(executive => (
                      <label key={executive.id} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={selectedPassengers.includes(executive.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedPassengers(prev => [...prev, executive.id]);
                            } else {
                              setSelectedPassengers(prev => prev.filter(id => id !== executive.id));
                            }
                          }}
                          className="h-4 w-4 text-ui-link border-ui-control rounded focus:ring-ui-primary"
                        />
                        <span className="ml-2 text-sm text-ui-ink">
                          {executive.first_name} {executive.last_name}
                          {executive.title && ` - ${executive.title}`}
                        </span>
                      </label>
                    ))
                  )}
                </div>
                <p className="text-xs text-ui-muted mt-2">
                  Select which executives will be guests at this hotel
                </p>
              </div>

              {/* Final Hotel Booking Button */}
              <div className="flex justify-between items-center pt-4 border-t">
                <div className="text-sm text-ui-ink-3">
                  Total: <span className="text-lg font-bold text-ui-green-fg">${selectedHotel.offers[0]?.price?.total}</span>
                  {selectedPassengers.length > 0 && (
                    <span className="ml-2">for {selectedPassengers.length} guest{selectedPassengers.length > 1 ? 's' : ''}</span>
                  )}
                </div>
                <button
                  onClick={() => bookHotelAsComponent(selectedHotel)}
                  disabled={loading || selectedPassengers.length === 0}
                  className="bg-ui-green-strong text-ui-on-primary px-6 py-3 rounded-md hover:bg-ui-green-strong/90 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
                >
                  {loading ? 'Booking...' : 'Book Hotel & Add to Trip'}
                </button>
              </div>
            </div>
          )}
          </div>

          {/* Terms Modal */}
          {showTermsModal && (
            <div className="fixed inset-0 bg-ui-ink bg-opacity-50 h-full w-full z-60 flex items-center justify-center">
              <div className="bg-ui-surface rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
                <div className="p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">
                      {termsType === 'terms' && 'Contract of Carriage'}
                      {termsType === 'baggage' && 'Baggage Policy'}
                      {termsType === 'cancellation' && 'Cancellation Policy'}
                      {airlineCode && ` - ${airlineCode}`}
                    </h3>
                    <button
                      onClick={() => setShowTermsModal(false)}
                      className="text-ui-muted hover:text-ui-ink-3"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <div className="prose text-sm">
                    {termsType === 'baggage' && (
                      <div>
                        <h4>Baggage Allowances</h4>
                        <ul>
                          <li>Personal item: FREE (under seat)</li>
                          <li>Carry-on bag: FREE or fee may apply (overhead bin)</li>
                          <li>Checked bags: Fees apply, varies by destination</li>
                          <li>Excess baggage fees apply beyond allowances</li>
                          <li>Size and weight restrictions apply</li>
                        </ul>
                        
                        <h4>Restricted Items</h4>
                        <ul>
                          <li>Liquids over 3.4 oz in carry-on</li>
                          <li>Sharp objects and weapons prohibited</li>
                          <li>Hazardous materials restrictions apply</li>
                        </ul>
                      </div>
                    )}
                    
                    {termsType === 'terms' && (
                      <div>
                        <h4>Contract of Carriage</h4>
                        <p>This contract governs your transportation and services provided by the airline.</p>
                        <ul>
                          <li>Tickets are non-transferable and valid only for the named passenger</li>
                          <li>Fares are subject to change until payment is made</li>
                          <li>Check-in deadlines must be met to avoid cancellation</li>
                          <li>Flight schedules are not guaranteed</li>
                          <li>Liability is limited under international conventions</li>
                        </ul>
                      </div>
                    )}
                    
                    {termsType === 'cancellation' && (
                      <div>
                        <h4>Cancellation & Change Policy</h4>
                        <ul>
                          <li>24-hour free cancellation policy (if applicable)</li>
                          <li>Change fees may apply depending on fare type</li>
                          <li>Non-refundable tickets may be eligible for future travel credit</li>
                          <li>Cancellation policies vary by fare class and destination</li>
                        </ul>
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-4 pt-4 border-t">
                    <button
                      onClick={() => setShowTermsModal(false)}
                      className="w-full bg-ui-ink text-ui-surface py-2 px-4 rounded-md hover:bg-ui-ink"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TripBookingModal;